'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAchievements,
  addCode,
  editAchievement,
} from '@/src/actions/profile';
import type { IAchievement } from 'types/src/types';

export const achievementsKeys = {
  all: ['achievements'] as const,
};

const toAchievements = (payload: any): IAchievement[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.achievements)) return payload.achievements;
  return [];
};

const normalizeError = (err: any): Error => {
  if (!err) return new Error('Unknown error');
  if (typeof err === 'string') return new Error(err);
  if (err?.message) return new Error(err.message);
  if (err?.error?.message) return new Error(err.error.message);
  try {
    return new Error(JSON.stringify(err));
  } catch {
    return new Error('Unknown error');
  }
};

async function fetchAchievements(): Promise<IAchievement[]> {
  const res = await getAchievements();
  return toAchievements(res);
}

export function useAchievementsQuery() {
  return useQuery({
    queryKey: achievementsKeys.all,
    queryFn: fetchAchievements,
  });
}

export function useAddCodeMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      const maybeError = await addCode(code);
      if (maybeError) {
        throw normalizeError(maybeError);
      }
      return true;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: achievementsKeys.all });
    },
  });
}

export function useEditAchievementMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isVisible }: { id: number; isVisible: boolean }) =>
      editAchievement(id, isVisible),
    onMutate: async ({ id, isVisible }) => {
      await qc.cancelQueries({ queryKey: achievementsKeys.all });
      const prev = qc.getQueryData<IAchievement[]>(achievementsKeys.all);
      qc.setQueryData<IAchievement[]>(achievementsKeys.all, (curr = []) =>
        curr.map((a) => (a.id === id ? { ...a, isVisible } : a))
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(achievementsKeys.all, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: achievementsKeys.all }),
  });
}
